import {Button, CrossIcon, DownloadIcon, List, ListItem, OpenIcon, toggleHtmlClass} from '@kit';

import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

import {Modal} from '../modal';
import {cls, Keys, throttle} from '../utils';
import {useGalleryContext} from './gallery-context';

import css from './gallery.module.css';

export const GalleryViewer: React.FC = () => {
	const ctx = useGalleryContext();
	const images = ctx?.images ?? [];
	const openId = ctx?.openId;
	const close = ctx?.close;
	const navigateTo = ctx?.navigateTo;

	const currentIndex = images.findIndex((i) => i.id === openId);
	const current = images[currentIndex] ?? null;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const galleryRef = useRef<HTMLDivElement>(null);
	const [zoom, setZoom] = useState({scale: 1, x: 0, y: 0});
	const zoomRef = useRef(zoom);

	const updateZoom = useCallback((value: typeof zoom) => {
		zoomRef.current = value;
		setZoom(value);
	}, []);

	useLayoutEffect(() => {
		updateZoom({scale: 1, x: 0, y: 0});
	}, [openId, updateZoom]);

	useEffect(() => {
		const wrapper = wrapperRef.current;
		const slide = wrapper?.children[currentIndex] as HTMLDivElement | undefined;
		const image = slide?.querySelector('img');
		if (!wrapper || !slide || !image) return;

		let lastTouch: {x: number; y: number} | null = null;
		let lastDistance = 0;
		let moved = false;
		let pinched = false;
		let lastTap: {x: number; y: number; time: number} | null = null;

		const point = (touch: Touch) => ({x: touch.clientX, y: touch.clientY});
		const distance = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
		const clamp = (scale: number, x: number, y: number) => {
			const limitX = Math.max(0, (image.clientWidth * scale - slide.clientWidth) / 2);
			const limitY = Math.max(0, (image.clientHeight * scale - slide.clientHeight) / 2);
			return {scale, x: Math.max(-limitX, Math.min(limitX, x)), y: Math.max(-limitY, Math.min(limitY, y))};
		};
		const zoomAt = (scale: number, clientX: number, clientY: number) => {
			if (scale === 1) {
				updateZoom({scale: 1, x: 0, y: 0});
				return;
			}
			const previous = zoomRef.current;
			const rect = slide.getBoundingClientRect();
			const centerX = clientX - (rect.left + rect.right) / 2;
			const centerY = clientY - (rect.top + rect.bottom) / 2;
			updateZoom(clamp(scale,
				centerX - (centerX - previous.x) * scale / previous.scale,
				centerY - (centerY - previous.y) * scale / previous.scale,
			));
		};

		const onStart = (event: TouchEvent) => {
			if (event.touches.length === 2) {
				lastDistance = distance(event.touches[0], event.touches[1]);
				pinched = true;
				lastTap = null;
			} else if (event.touches.length === 1) {
				lastTouch = point(event.touches[0]);
				moved = false;
				pinched = false;
			}
		};

		const onMove = (event: TouchEvent) => {
			if (event.touches.length === 2) {
				event.preventDefault();
				const [a, b] = [event.touches[0], event.touches[1]];
				const nextDistance = distance(a, b);
				if (lastDistance) {
					const scale = Math.max(1, Math.min(4, zoomRef.current.scale * nextDistance / lastDistance));
					zoomAt(scale, (a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
				}
				lastDistance = nextDistance;
				pinched = true;
				return;
			}
			if (event.touches.length !== 1 || !lastTouch) return;
			const next = point(event.touches[0]);
			if (Math.hypot(next.x - lastTouch.x, next.y - lastTouch.y) > 2) moved = true;
			if (zoomRef.current.scale > 1) {
				event.preventDefault();
				const {scale, x, y} = zoomRef.current;
				updateZoom(clamp(scale, x + next.x - lastTouch.x, y + next.y - lastTouch.y));
			}
			lastTouch = next;
		};

		const onEnd = (event: TouchEvent) => {
			lastDistance = 0;
			if (event.touches.length === 1) {
				lastTouch = point(event.touches[0]);
				return;
			}
			lastTouch = null;
			if (pinched || moved || event.changedTouches.length !== 1) {
				lastTap = null;
				return;
			}
			const tap = {...point(event.changedTouches[0]), time: Date.now()};
			if (lastTap && tap.time - lastTap.time < 300 && Math.hypot(tap.x - lastTap.x, tap.y - lastTap.y) < 30) {
				event.preventDefault();
				updateZoom({scale: 1, x: 0, y: 0});
				lastTap = null;
			} else {
				lastTap = tap;
			}
		};

		const onCancel = () => {
			lastTouch = null;
			lastDistance = 0;
			lastTap = null;
			pinched = false;
		};

		const onWheel = (event: WheelEvent) => {
			if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && !event.ctrlKey) return;
			event.preventDefault();
			const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? slide.clientHeight : 1);
			const step = Math.max(-120, Math.min(120, delta));
			const scale = Math.max(1, Math.min(4, zoomRef.current.scale * Math.exp(-step * 0.0007)));
			zoomAt(scale, event.clientX, event.clientY);
		};

		wrapper.addEventListener('wheel', onWheel, {passive: false});
		wrapper.addEventListener('touchstart', onStart, {passive: true});
		wrapper.addEventListener('touchmove', onMove, {passive: false});
		wrapper.addEventListener('touchend', onEnd, {passive: false});
		wrapper.addEventListener('touchcancel', onCancel);
		return () => {
			wrapper.removeEventListener('wheel', onWheel);
			wrapper.removeEventListener('touchstart', onStart);
			wrapper.removeEventListener('touchmove', onMove);
			wrapper.removeEventListener('touchend', onEnd);
			wrapper.removeEventListener('touchcancel', onCancel);
		};
	}, [currentIndex, openId, updateZoom]);

	const handleNext = useCallback(() => {
		const next = images[currentIndex + 1 >= images.length ? 0 : currentIndex + 1];
		if (next && navigateTo) navigateTo(next.id);
	}, [currentIndex, images, navigateTo]);

	const handlePrev = useCallback(() => {
		const prev = images[currentIndex === 0 ? images.length - 1 : currentIndex - 1];
		if (prev && navigateTo) navigateTo(prev.id);
	}, [currentIndex, images, navigateTo]);

	useEffect(() => {
		if (!openId) return;
		toggleHtmlClass(true);
		return () => toggleHtmlClass(false);
	}, [openId]);

	useEffect(() => {
		if (!wrapperRef.current || currentIndex < 0) return;
		wrapperRef.current.scrollLeft = currentIndex * wrapperRef.current.clientWidth;
	}, [currentIndex]);

	useEffect(() => {
		if (!galleryRef.current || currentIndex < 0) return;
		const gallery = galleryRef.current;
		const selectedEl = gallery.children[currentIndex] as HTMLElement;
		if (!selectedEl) return;
		gallery.scrollTo({
			left: selectedEl.offsetLeft - gallery.clientWidth / 2 + selectedEl.offsetWidth / 2,
			behavior: 'smooth',
		});
	}, [currentIndex]);

	useEffect(() => {
		// стрелки листают галерею только пока она открыта: иначе они бы открывали её из любой формы
		if (!openId) return;

		function checkKey(e: KeyboardEvent) {
			if (e.keyCode === Keys.ESCAPE) {
				if (zoomRef.current.scale > 1) updateZoom({scale: 1, x: 0, y: 0});
				else close?.();
			}
			if (e.keyCode === Keys.LEFT_ARROW) handlePrev();
			if (e.keyCode === Keys.RIGHT_ARROW) handleNext();
		}
		document.addEventListener('keyup', checkKey);
		return () => document.removeEventListener('keyup', checkKey);
	}, [openId, close, handlePrev, handleNext, updateZoom]);

	const handleScroll = useCallback(
		throttle(() => {
			if (!wrapperRef.current || !navigateTo || zoomRef.current.scale > 1) return;
			const scrollLeft = wrapperRef.current.scrollLeft;
			const imageElements = wrapperRef.current.children;
			for (let i = 0; i < imageElements.length; i++) {
				const el = imageElements.item(i) as HTMLDivElement;
				if (Math.round(el?.offsetLeft) - Math.round(scrollLeft) === 0) {
					const id = el.dataset.id;
					if (id && id !== openId) navigateTo(id);
					break;
				}
			}
		}, 200),
		[openId, navigateTo],
	);

	// якорь может указывать на картинку, которой на этой странице нет: пустую галерею не открываем
	if (!openId || !current) return null;

	return (
		<Modal>
			<div className={css.wrapper}>
				<div className={css.header}>
					{current && (
						<>
							<Button
								appearance="subtle"
								icon={<OpenIcon />}
								href={current.url}
								target="_blank"
								rel="noreferrer"
								aria-label="Открыть оригинал"
								title="Открыть оригинал"
							/>
							<Button
								appearance="subtle"
								icon={<DownloadIcon />}
								href={current.url}
								download
								aria-label="Скачать"
								title="Скачать"
							/>
						</>
					)}
					<Button appearance="subtle" icon={<CrossIcon />} onClick={close} aria-label="Закрыть" />
				</div>

				<div className={css.stage}>
					<div className={cls(css.images, zoom.scale > 1 && css.zoomed)} ref={wrapperRef} onScroll={handleScroll}>
						{images.map((image) => (
							<div
									className={css.imageWrapper}
									data-id={image.id}
									key={image.id}
									onClick={(e) => e.target === e.currentTarget && close?.()}
								>
								<img
									className={css.image}
									src={image.url}
									alt={image.description}
									draggable={false}
									style={image.id === openId ? {transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`} : undefined}
									onDoubleClick={() => updateZoom({scale: 1, x: 0, y: 0})}
								/>
							</div>
						))}
					</div>
					{(current?.credits || current?.description) && (
						<div className={css.title}>
							{current.credits && (
								<footer>
									<small>{current.credits}</small>
								</footer>
							)}
							{current.description}
						</div>
					)}
				</div>
				<List
					forwardRef={galleryRef}
					horizontal
					navigationMode="items"
					selectMode="single"
					selectedItems={openId ? [openId] : []}
					className={css.gallery}
				>
					{images.map((image) => (
						<ListItem
							key={image.id}
							value={image.id}
							onAction={() => navigateTo?.(image.id)}
							className={css.galleryItem}
							buttonClassName={css.galleryItemButton}
							selectedClassName={css.galleryItemSelected}
						>
							<img src={image.preview} alt={image.description || ''} width={100} height={80} />
						</ListItem>
					))}
				</List>
			</div>
		</Modal>
	);
};
